import { Send, Trophy } from 'lucide-react'
import { useState } from 'react'
import { useSendWinner } from '@/api/hooks/use-campaigns'
import { Modal } from '@/components/ui/modal'
import type { CampaignVariant } from '@/types/newsletter'

interface CampaignVariantStatsProps {
  campaignId: string
  variants: CampaignVariant[]
}

function formatRate(numerator: number, denominator: number): string {
  if (denominator === 0) return '0%'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

function findWinner(variants: CampaignVariant[]): CampaignVariant | null {
  if (variants.length < 2) return null
  const withDelivered = variants.filter((v) => v.totalDelivered > 0)
  if (withDelivered.length < 2) return null

  let best: CampaignVariant = withDelivered[0] as CampaignVariant
  for (const v of withDelivered) {
    if (v.openRate > best.openRate) {
      best = v
    }
  }
  return best
}

function computeOpenRateDelta(winner: CampaignVariant, variants: CampaignVariant[]): string {
  const others = variants.filter((v) => v.id !== winner.id && v.totalDelivered > 0)
  if (others.length === 0) return ''
  const avgOtherRate = others.reduce((sum, v) => sum + v.openRate, 0) / others.length
  const delta = winner.openRate - avgOtherRate
  return `+${delta.toFixed(1)}% open rate`
}

export function CampaignVariantStats({
  campaignId,
  variants,
}: CampaignVariantStatsProps): React.JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false)
  const sendWinner = useSendWinner()
  const winner = findWinner(variants)

  function handleSendWinner(): void {
    if (!winner) return
    sendWinner.mutate(
      { campaignId, variantId: winner.id },
      { onSuccess: () => setShowConfirm(false) },
    )
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h3 className="mb-4 text-sm font-medium uppercase tracking-wider text-text-secondary">
        A/B Test Results
      </h3>

      {/* Variant cards grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {variants.map((variant) => (
          <VariantStatCard
            key={variant.id}
            variant={variant}
            isWinner={winner?.id === variant.id}
          />
        ))}
      </div>

      {/* Winner summary */}
      {winner && (
        <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-text-primary">
            <span className="font-semibold">Winner: Variant {winner.name}</span>
            <span className="ml-1 text-text-secondary">
              ({computeOpenRateDelta(winner, variants)})
            </span>
          </p>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            <Send className="h-4 w-4" />
            Send Winner to Remaining
          </button>
        </div>
      )}

      {/* Confirmation modal */}
      <Modal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Send Winning Variant"
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          Are you sure you want to send <strong>Variant {winner?.name}</strong> to the remaining
          recipients who have not yet received this campaign? This action cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSendWinner}
            disabled={sendWinner.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {sendWinner.isPending ? 'Sending...' : 'Send Winner'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

interface VariantStatCardProps {
  variant: CampaignVariant
  isWinner: boolean
}

function VariantStatCard({ variant, isWinner }: VariantStatCardProps): React.JSX.Element {
  return (
    <div
      className={`rounded-lg border p-4 ${
        isWinner ? 'border-brand-500 bg-brand-500/5' : 'border-surface-3 bg-surface-1'
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-text-primary">Variant {variant.name}</span>
          <span className="ml-2 text-xs text-text-tertiary">{variant.weight}% weight</span>
        </div>
        {isWinner && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-xs font-medium text-white">
            <Trophy className="h-3 w-3" />
            Winner
          </span>
        )}
      </div>

      <dl className="space-y-1.5">
        <StatRow label="Sent" value={variant.totalRecipients.toLocaleString()} />
        <StatRow
          label="Open rate"
          value={formatRate(variant.totalOpened, variant.totalDelivered)}
          highlight={isWinner}
        />
        <StatRow
          label="Click rate"
          value={formatRate(variant.totalClicked, variant.totalDelivered)}
        />
        <StatRow
          label="Bounce rate"
          value={formatRate(variant.totalBounced, variant.totalRecipients)}
        />
      </dl>
    </div>
  )
}

interface StatRowProps {
  label: string
  value: string
  highlight?: boolean
}

function StatRow({ label, value, highlight }: StatRowProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-xs text-text-tertiary">{label}</dt>
      <dd className={`text-sm font-medium ${highlight ? 'text-brand-700' : 'text-text-primary'}`}>
        {value}
      </dd>
    </div>
  )
}
